'use client';
import { useEffect } from 'react';
import Link from 'next/link';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { handleErrorToasts } from './errorToasts';
import { Button } from './Button';

type NextError = Error & { digest?: string };

export type ErrorBoundaryProps = {
  error: NextError;
  /** Tries to re-render the Error boundary's contents. If successful, the fallback
   * error component is replaced with the result of the re-render. */
  reset: () => void;
};

type Props = ErrorBoundaryProps & {
  homeUrl: string;
  onError?: (error: NextError) => any;
};

export const ErrorBoundary = ({ error, onError, reset, homeUrl }: Props) => {
  useEffect(() => {
    handleErrorToasts(error.message);
    // Log the error to an error reporting service
    onError?.(error);
  }, [error]);

  return (
    <div
      role="alert"
      className="flex h-full flex-col items-center justify-center gap-16 p-16"
    >
      <div className="flex flex-col items-center gap-8">
        <div className="flex items-center gap-2 text-4xl">
          <ExclamationCircleIcon className="size-9" />
          <h2 className="">We've encountered an issue</h2>
        </div>
        <p>
          Please try again later or contact support if the problem persists.
        </p>
        <div className="space-x-3 py-2">
          <Button className="btn btn-outline" onClick={reset}>
            Try again
          </Button>
          <Link href={homeUrl}>
            <Button className="btn btn-primary">Go home</Button>
          </Link>
        </div>
      </div>
      {process.env.NODE_ENV === 'development' && (
        <pre className="max-w-full overflow-auto rounded-lg border bg-gray-700 p-6 text-red-300 shadow-sm">
          {error.message}
          <div className="my-4 font-light text-gray-300">
            Reference Digest ID: {error.digest}
          </div>
          <div className="my-6 font-light text-gray-400">
            (See console for stack trace. This box and logs are not shown in
            production environments.)
          </div>
        </pre>
      )}
    </div>
  );
};
